import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Transcription} from '../domain/types';
import {useToast} from './Toast';
import {downloadText} from '../lib/browserFiles';
import {shouldLogout} from '../lib/auth-error-handler';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {signOut} from '../store/slices/authThunks';
import {debugWarn} from '../lib/debug';
import {
  fetchUserWordlists,
  fetchUserWordlistsCollection,
} from '../store/slices/wordlistsThunks';
import {
  myWordlistsSelector,
} from '../store/selectors/wordlists.selectors';
import {convertCsvToXml, deleteConversion, downloadConversionXml} from '../store/slices/conversionsThunks';
import {isApiError} from '../lib/apiError';
import WordListMultiSelect from './workspace/WordListMultiSelect';
import {useOwnedProjectId} from './useOwnedProjectId';
import {fetchTranscriptions} from '../store/slices/transcriptionsThunks';
import {setCurrentProjectThunk, fetchProjects} from '../store/slices/projectsThunks';
import {transcriptionIsLoadingSelector, completedTranscriptionsSelector} from '../store/selectors/transcription.selectors';
import {ConversionHistory} from './convertComponents/ConversionHistory';
import {projectConversionsDataSelector} from '../store/selectors/conversions.selectors';

/** Refusals that are about the selection rather than any one file — see `handleStartBatchConversion`. */
const WORDLIST_REFUSALS = ['wordlist_not_found', 'too_many_wordlists', 'invalid_wordlist_ids'];

interface BatchConversionItem {
    id: string;
    transcription: Transcription;
    status: 'pending' | 'processing' | 'completed' | 'error';
    result?: string;
    error?: string;
    censoredWords?: number;
    totalCuts?: number;
}

const BatchConvertContainer: React.FC = () => {
  const {showToast} = useToast();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {projectId: routeProjectId} = useParams<{projectId: string}>();

  const collectionWordlists = useAppSelector(myWordlistsSelector);
  // Unfinished transcriptions are deliberately not offered: their CSV does not exist yet.
  const transcriptions = useAppSelector(completedTranscriptionsSelector);
  const isLoading = useAppSelector(transcriptionIsLoadingSelector);
  const conversions = useAppSelector(projectConversionsDataSelector);

  // Route param first, Redux as the fallback — but only if this account owns it. The previous
  // version paired a route id with whatever `currentProject` happened to be, so an id from another
  // account went out with a valid session behind it. See `useOwnedProjectId`.
  const {projectId, project, isResolving: isResolvingProject} = useOwnedProjectId(routeProjectId);

  const [selectedTranscriptions, setSelectedTranscriptions] = useState<Set<string>>(new Set());
  const [batchItems, setBatchItems] = useState<BatchConversionItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

    // Conversion settings.
    // There is no output folder or beep sound on the web: each finished XML is downloaded by the
    // browser, and beep sounds are pending an uploadable library (docs/BACKEND_REQUESTS.md).
  const [fps, setFps] = useState<number>(29.97);
  const [markerOption, setMarkerOption] = useState<'none' | 'markers' | 'markers-with-word'>('markers');
  // Up to five lists, merged and de-duplicated server-side. Every item in the run uses the same
  // set — the whole point of the batch screen is that the settings are chosen once.
  const [selectedWordListIds, setSelectedWordListIds] = useState<string[]>([]);
  // The verbatim message from a convert refused over the lists (`wordlist_not_found` and friends).
  // It applies to the whole run rather than to one item, so it sits by the picker, not in the rows.
  const [wordListError, setWordListError] = useState<{message: string; missing: string[]} | null>(null);

  // Redirect if no projectId available — but not while ownership is still being resolved, which is
  // a transient "no project" rather than a real one.
  useEffect(() => {
    if (!projectId && !isResolvingProject) {
      navigate('/projects', {replace: true});
      return;
    }
  }, [projectId, isResolvingProject, navigate]);

  // A project was asked for but this account's list has not arrived yet — go and get it, which is
  // also what settles whether the project is ours at all.
  useEffect(() => {
    if (isResolvingProject) {
      dispatch(fetchProjects());
    }
  }, [isResolvingProject, dispatch]);

  // Load data for the project
  useEffect(() => {
    if(projectId && project) {
      dispatch(fetchTranscriptions(projectId));
      dispatch(fetchUserWordlists());
      dispatch(fetchUserWordlistsCollection());
      // This will load working folders into Redux state
      dispatch(setCurrentProjectThunk(project));
    }
  }, [projectId, project, dispatch]);

  // Seed the selection once the lists land — the default list if there is one, otherwise the first.
  // Still one list, not five: a batch that silently censored against everything the user owns is
  // not what picking nothing should mean.
  useEffect(() => {
    if (collectionWordlists.length > 0 && selectedWordListIds.length === 0) {
      const defaultWordlist = collectionWordlists.find(wl => wl.isDefault);
      setSelectedWordListIds([(defaultWordlist ?? collectionWordlists[0]).id]);
    }
  }, [collectionWordlists, selectedWordListIds]);

  const handleSelectAll = () => {
    if (selectedTranscriptions.size === filteredTranscriptions.length) {
      setSelectedTranscriptions(new Set());
    } else {
      setSelectedTranscriptions(new Set(filteredTranscriptions.map(t => t.id)));
    }
  };

  const handleToggleTranscription = (id: string) => {
    const newSelection = new Set(selectedTranscriptions);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedTranscriptions(newSelection);
  };

  const handleStartBatchConversion = async () => {
    if (selectedTranscriptions.size === 0) {
      showToast('Please select at least one transcription', 'error');
      return;
    }

    if (selectedWordListIds.length === 0) {
      showToast('Please select at least one word list', 'error');
      return;
    }

    setIsProcessing(true);
    setWordListError(null);

        // Create batch items
    const items: BatchConversionItem[] = Array.from(selectedTranscriptions).map((id) => {
      const transcription = transcriptions.find(t => t.id === id)!;
      return {
        id,
        transcription,
        status: 'pending' as const
      };
    });
    setBatchItems(items);

        // Process each item sequentially
    let completedCount = 0;
    let errorCount = 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

            // Update status to processing
      setBatchItems(prev =>
        prev.map(b => b.id === item.id ? {...b, status: 'processing' as const} : b)
      );

      try {
        // Validate required fields
        if (!item.transcription.csvR2Key) {
          throw new Error('CSV R2 key not available');
        }

        if (!projectId) {
          throw new Error('Project ID not available');
        }

                // Sample rate and duration have to be probed server-side at upload time; the
                // browser cannot read them off the source audio. See docs/BACKEND_REQUESTS.md.
        const {sampleRate, duration} = item.transcription;
        if (sampleRate === undefined || duration === undefined) {
          throw new Error('No audio sample rate or duration recorded for this transcription');
        }

                // The media reference is required, not optional: a clip that names no file at all
                // cannot be relinked in any editor afterwards, however the import goes.
                // There is no field to fill in here, so this falls back the same way
                // `conversionSourcesSelector` does: the local machine path first (it relinks on its
                // own), then the uploaded file's name (audio_filename — always set for a server-side
                // transcription even when audioPath, the local path, never was). Only a transcription
                // with neither fails loudly instead of producing that file.
        const audioPath = item.transcription.audioPath?.trim() || item.transcription.audioFilename?.trim();
        if (!audioPath) {
          throw new Error(
            'No source audio file name recorded for this transcription. Convert it from the '
            + 'workspace, where you can type one in.'
          );
        }

                // Generate output filename from transcription name
        let outputFileName = item.transcription.name.replace(/\.[^/.]+$/, ''); // Remove extension
        if (!outputFileName.trim()) {
          throw new Error('This transcription has no name to give the XML.');
        }
        if (!outputFileName.toLowerCase().endsWith('.xml')) {
          outputFileName += '.xml';
        }

                // Determine marker mode
        const markerMode = markerOption === 'markers-with-word'
          ? 'with-word'
          : markerOption === 'markers'
            ? 'clean'
            : 'none';

                // Call conversion thunk with R2 key (not local path)
        const result = await dispatch(convertCsvToXml({
          csvKey: item.transcription.csvR2Key,
          audioPath,
          audioSampleRate: sampleRate,
          audioDuration: duration,
          projectId: projectId || undefined,
          // Beep sounds are not available on the web yet.
          mode: 'silence',
          markerMode: markerMode as 'word' | 'clean' | null,
          wordlistIds: selectedWordListIds,
          fps: fps,
          outputFileName,
        })).unwrap();

                // Fetch the finished XML and hand it to the browser as a download.
                // Name collisions are the browser's problem now — it suffixes duplicates itself,
                // which replaces the desktop app's "-copy-N" logic.
                //
                // `metadata.xml_url` is the API route, not a presigned R2 URL, so a bare fetch()
                // gets a 401 — it has to go through the thunk that attaches the JWT, the same one
                // every other download button uses.
        const xmlContent = await dispatch(downloadConversionXml({
          conversionId: result.conversion.id,
        })).unwrap();

        downloadText(xmlContent, outputFileName, 'application/xml');

        const resultMessage = `✅ XML generated successfully with ${result.metadata.total_cuts} cuts`;

                // Update status to completed
        setBatchItems(prev =>
          prev.map(b => b.id === item.id
            ? {...b, status: 'completed' as const, result: resultMessage, censoredWords: result.metadata.censored_words, totalCuts: result.metadata.total_cuts}
            : b
          )
        );

        completedCount++;
      } catch (err) {
        console.error(`Error converting ${item.transcription.name}:`, err);

                // Check for 401/auth errors and auto-logout
        if (shouldLogout(err)) {
          debugWarn('Authentication error - logging out');
          dispatch(signOut());
          showToast('Session expired. Please log in again.', 'error');
          setIsProcessing(false);
          return; // Stop batch processing
        }

        // A refused word-list selection is not this item's problem — it is the run's, and every
        // remaining item would fail the same way. Stop, put the server's sentence by the picker
        // (with the offending lists marked) and let the user fix it once.
        if (isApiError(err) && WORDLIST_REFUSALS.includes(err.reason ?? '')) {
          setWordListError({message: err.message, missing: err.missing ?? []});
          setBatchItems(prev =>
            prev.map(b => b.id === item.id
              ? {...b, status: 'error' as const, error: err.message}
              : b
            )
          );
          showToast(err.message, 'error', 8000);
          setIsProcessing(false);
          return;
        }

                // Update status to error
        setBatchItems(prev =>
          prev.map(b => b.id === item.id
            ? {...b, status: 'error' as const, error: String(err)}
            : b
          )
        );

        errorCount++;
      }
    }

    setIsProcessing(false);

    if (errorCount === 0) {
      showToast(`Successfully converted ${completedCount} files!`, 'success');
    } else {
      showToast(`Completed with ${completedCount} success, ${errorCount} errors`, 'error');
    }
  };

  const handleClearBatch = () => {
    setBatchItems([]);
    setSelectedTranscriptions(new Set());
  };

  const filteredTranscriptions = transcriptions.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.audioPath.toLowerCase().includes(searchTerm.toLowerCase())
  );


  if (isLoading) {
    return (
      <div className="h-full bg-gradient-to-br from-gray-900 via-green-900 to-gray-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-green-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
          <span className="text-lg">Loading transcriptions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-gray-900 via-green-900 to-gray-900 overflow-auto">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">
                        🎬 {project ? `${project.name} - Batch Convert` : 'Batch Convert to XML'}
          </h1>
          <p className="text-gray-400">
                        Convert multiple CSV transcriptions to Final Cut Pro XML files at once.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Selection */}
          <div className="space-y-4">
            <div className="bg-gray-800 rounded-lg shadow-2xl p-5 border border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Select Transcriptions</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => dispatch(fetchTranscriptions(projectId!))}
                    className="px-3 py-1.5 bg-blue-700 text-white text-sm rounded hover:bg-blue-600 transition-colors cursor-pointer"
                    title="Refresh transcriptions"
                  >
                    🔄 Refresh
                  </button>
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1.5 bg-gray-700 text-gray-300 text-sm rounded hover:bg-gray-600 transition-colors cursor-pointer"
                    disabled={filteredTranscriptions.length === 0}
                  >
                    {selectedTranscriptions.size === filteredTranscriptions.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>

              {/* Search */}
              {transcriptions.length > 0 && (
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="🔍 Search transcriptions..."
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white text-sm rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white cursor-pointer"
                      >
                                                ✕
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Transcriptions List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredTranscriptions.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    {searchTerm ? 'No transcriptions match your search' : 'No transcriptions available'}
                  </div>
                ) : (
                  filteredTranscriptions.map(transcription => (
                    <div
                      key={transcription.id}
                      onClick={() => handleToggleTranscription(transcription.id)}
                      className={`p-3 rounded border cursor-pointer transition-all ${selectedTranscriptions.has(transcription.id)
                        ? 'bg-green-900/30 border-green-600'
                        : 'bg-gray-700 border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedTranscriptions.has(transcription.id)}
                          onChange={() => { }}
                          className="mt-1 cursor-pointer"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium text-sm truncate">
                            {transcription.name}
                          </div>
                          <div className="text-xs text-gray-400 truncate mt-1">
                                                        📊 CSV: {transcription.csvR2Key ? transcription.csvR2Key.split('/').pop() : 'Pending'}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {new Date(transcription.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {selectedTranscriptions.size > 0 && (
                <div className="mt-4 p-3 bg-green-900/20 border border-green-600/30 rounded">
                  <p className="text-green-300 text-sm">
                                        ✓ Selected: <span className="font-bold">{selectedTranscriptions.size}</span> transcription{selectedTranscriptions.size !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Settings */}
          <div className="space-y-4">
            <div className="bg-gray-800 rounded-lg shadow-2xl p-5 border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Conversion Settings</h2>

              <div className="space-y-3">
                {/* Output note — the web app downloads each finished XML instead of
                    writing to a folder */}
                <div className="px-3 py-2 bg-gray-800 border border-gray-700 border-dashed rounded">
                  <p className="text-xs text-gray-400">
                    📥 Each finished XML downloads to your browser. Beep sounds aren&apos;t
                    available yet, so conversions cut to silence.
                  </p>
                </div>

                {/* Word lists — the same picker the workspace dialog uses, so the cap, the
                    descriptions and the "no longer available" marking behave identically here. */}
                <div>
                  <WordListMultiSelect
                    lists={collectionWordlists}
                    selectedIds={selectedWordListIds}
                    onChange={setSelectedWordListIds}
                    missingIds={wordListError?.missing ?? []}
                    invalid={Boolean(wordListError)}
                    disabled={isProcessing}
                  />
                  {wordListError && (
                    <div className="mt-2 rounded border border-red-500/30 bg-red-500/[0.06] px-3 py-2
                     text-xs text-red-200">
                      {wordListError.message}
                    </div>
                  )}
                </div>

                {/* FPS */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor="batch-fps">
                                        Frames Per Second (FPS)
                  </label>
                  <input
                    id="batch-fps"
                    type="number"
                    step="0.01"
                    value={fps}
                    onChange={e => setFps(parseFloat(e.target.value))}
                    disabled={isProcessing}
                    className="w-32 px-3 py-2 bg-gray-700 border border-gray-600 text-white text-sm rounded focus:outline-none focus:ring-2 focus:ring-green-500 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Marker Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor="batch-marker-option">
                                        Marker Options
                  </label>
                  <select
                    id="batch-marker-option"
                    value={markerOption}
                    onChange={e => setMarkerOption(e.target.value as any)}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white text-sm rounded focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer disabled:cursor-not-allowed"
                  >
                    <option value="none">None</option>
                    <option value="markers">Add markers (shows word was censored)</option>
                    <option value="markers-with-word">Add markers with censored word</option>
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="pt-4 space-y-2">
                  <button
                    onClick={handleStartBatchConversion}
                    disabled={isProcessing || selectedTranscriptions.size === 0 || selectedWordListIds.length === 0}
                    className={'w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm font-semibold rounded '
                      + 'hover:from-green-500 hover:to-emerald-500 disabled:from-gray-600 disabled:to-gray-600 transition-all shadow '
                      + 'cursor-pointer disabled:cursor-not-allowed'}
                  >
                    {isProcessing ? '⏳ Converting...' : `🎬 Convert ${selectedTranscriptions.size} File${selectedTranscriptions.size !== 1 ? 's' : ''}`}
                  </button>

                  {batchItems.length > 0 && !isProcessing && (
                    <button
                      onClick={handleClearBatch}
                      className="w-full px-4 py-2 bg-gray-700 text-gray-300 text-sm font-medium rounded hover:bg-gray-600 transition-colors cursor-pointer"
                    >
                                            Clear Results
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Batch Progress */}
        {batchItems.length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg shadow-2xl p-5 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Conversion Progress</h2>
              <div className="flex gap-4 text-sm">
                <span className="text-gray-400">
                                    Completed: <span className="text-green-400 font-bold">{batchItems.filter(b => b.status === 'completed').length}</span>
                </span>
                <span className="text-gray-400">
                                    Errors: <span className="text-red-400 font-bold">{batchItems.filter(b => b.status === 'error').length}</span>
                </span>
                <span className="text-gray-400">
                                    Total: <span className="text-white font-bold">{batchItems.length}</span>
                </span>
              </div>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {batchItems.map(item => (
                <div
                  key={item.id}
                  className={`p-4 rounded border ${item.status === 'completed' ? 'bg-green-900/20 border-green-600/30' :
                    item.status === 'error' ? 'bg-red-900/20 border-red-600/30' :
                      item.status === 'processing' ? 'bg-blue-900/20 border-blue-600/30' :
                        'bg-gray-700 border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-white font-medium text-sm">
                          {item.transcription.name}
                        </span>
                        {item.status === 'completed' && <span className="text-green-400">✓</span>}
                        {item.status === 'error' && <span className="text-red-400">✗</span>}
                        {item.status === 'processing' && <span className="text-blue-400 animate-pulse">⏳</span>}
                        {item.status === 'pending' && <span className="text-gray-400">⏸</span>}
                      </div>

                      {item.status === 'completed' && item.censoredWords !== undefined && (
                        <div className="flex gap-4 text-xs mb-2">
                          <span className="text-red-400">
                                                        Censored: <span className="font-bold">{item.censoredWords}</span>
                          </span>
                          <span className="text-yellow-400">
                                                        Cuts: <span className="font-bold">{item.totalCuts}</span>
                          </span>
                        </div>
                      )}

                      {item.error && (
                        <div className="text-xs text-red-400 mt-2">
                                                    Error: {item.error}
                        </div>
                      )}

                      {item.result && (
                        <div className="text-xs text-gray-400 mt-2 truncate">
                          {item.result.split('\n')[0]}
                        </div>
                      )}
                    </div>

                    <div className="text-xs font-semibold uppercase">
                      {item.status === 'completed' && <span className="text-green-400">Done</span>}
                      {item.status === 'error' && <span className="text-red-400">Failed</span>}
                      {item.status === 'processing' && <span className="text-blue-400">Processing</span>}
                      {item.status === 'pending' && <span className="text-gray-400">Pending</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Conversions History */}
        <ConversionHistory
          conversions={conversions}
          showConversions={true}
          onToggle={() => {}} // No-op since we always show conversions
          onDelete={async (conversionId: string) => {
            await dispatch(deleteConversion(conversionId)).unwrap();
          }}
        />
      </div>
    </div>
  );
};

export default BatchConvertContainer;
